/* The shell: the teal rail + the white top bar + a body that fills the viewport (ADR-0212).

   The load-bearing rule is that **the page never scrolls**. `html, body, #root` are height 100%
   with `overflow: hidden` in tokens.css; here every ancestor of a scrolling panel carries
   `min-height: 0`, because a flex or grid child defaults to `min-height: auto` and will refuse to
   shrink below its content - which silently pushes the grid past the viewport and gives the page
   a scrollbar. That single property is the difference between panels scrolling inside themselves
   and the whole page scrolling.

   **The look (KD, 2026-11-01).** A solid theme-teal rail of 232px (60px as icons) with white text,
   the current place a white-filled row with teal text, section headings small and muted-light; a
   white 52px bar with hairline; a white page, on which cards are told apart by a 1px border and 10px
   radius (`.card` in tokens.css). Only the rail is teal. The bar and the page are `--panel`, the
   rail's are `--rail-*`; nothing here is a colour literal (ADR-0007).

   Hooks the tests and the browser gate read survive: `[class*="_rail_"]`, `[class*="_nav_"]` (the
   strip, and only the strip), `[class*="_role_"]` (the person's role, in UserMenu.module.css) and
   the `header` being the first one in the document. Class names containing `_nav_` or `_role_` are
   substring-matched by `ui-check`, so a new class must not be named so that it contains either.
   `railSlot` deliberately does not contain `_rail_`. */

.shell {
  --rail: 232px;
  --top: 52px;
  --chip-h: 30px;
  --chip-r: 8px;

  display: flex;
  height: 100%;
  min-height: 0;
}

/* ---------------------------------------------------------------- rail */

/* The slot keeps the rail's place in the layout: full width, or icons when collapsed. The rail
   fills it - and on a record page, where it is icons, pointing at it opens the rail over the page
   (`data-peek`) without the page moving. At phone width the rail is a menu and the slot vanishes. */
.railSlot {
  position: relative;
  display: flex;
  flex: 0 0 var(--rail);
  width: var(--rail);
  min-height: 0;
}

.railSlot[data-collapsed="true"] {
  flex-basis: var(--rail-min);
  width: var(--rail-min);
}

.railSlot[data-peek="true"] .rail {
  position: absolute;
  z-index: 30;
  inset: 0 auto 0 0;
  width: var(--rail);
  box-shadow: var(--shadow-drawer);
}

.rail {
  flex: 1 1 auto;
  width: 100%;
  background: var(--rail-bg);
  color: var(--rail-ink);
  padding: 0 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
}

/* The brand row is exactly as tall as the bar, so the mark and the title sit on one line across the
   top of the page. */
.brandRow {
  display: flex;
  align-items: center;
  height: var(--top);
  flex: 0 0 var(--top);
  padding: 0 6px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-weight: 700;
  font-size: var(--t-body);
  letter-spacing: -0.02em;
  color: var(--rail-ink);
}

/* A letter on the accent. Not a glyph: the mark is the product's name and not an icon, and a
   hand-drawn path standing in for a logo nobody has supplied would be the placeholder `.navDot`
   once was. */
.brandMark {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: var(--chip-r);
  /* A white tile with teal ink on the teal rail. */
  background: var(--rail-active);
  color: var(--rail-active-ink);
  font-size: var(--t-min);
  font-weight: 700;
  letter-spacing: 0;
  flex-shrink: 0;
}

.brandName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The strip scrolls inside the rail when the viewport is short, so the workspace card below it is
   never pushed off - and **it says it scrolls** (F19). The first two backgrounds are covers that
   scroll with the content and hide the shadows when the strip is at that end; the last two are
   shadows pinned to the edges. Where there is more above or below, a hairline shades the edge. A
   thin scrollbar as well, because a classic 15px one would take a tenth of the rail. */
.nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  min-height: 0;
  padding: 2px 0;
  scrollbar-width: thin;
  flex: 1 1 auto;
  scrollbar-color: var(--rail-line) transparent;
  background:
    linear-gradient(var(--rail-bg-top) 30%, transparent) top / 100% 18px no-repeat local,
    linear-gradient(transparent, var(--rail-bg-bottom) 70%) bottom / 100% 18px no-repeat local,
    linear-gradient(var(--rail-hover), transparent) top / 100% 6px no-repeat scroll,
    linear-gradient(transparent, var(--rail-hover)) bottom / 100% 6px no-repeat scroll;
}

/* A group is a chunk with a head: the label runs into a hairline, and the air above it is what
   separates one chunk from the last. Case is CSS, not source: the browser gate compares the
   rendered text case-insensitively for exactly that reason. */
.navBlock {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.navBlock + .navBlock {
  margin-top: 6px;
}

.navGroup {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 6px 2px 8px;
  color: var(--rail-muted);
  font-size: var(--t-min);
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 16px;
  text-transform: uppercase;
}

.navGroup::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--rail-line);
}

/* **Position is shown three ways and none is colour alone**: a white-filled row, teal text on it
   and heavier weight. Hover is a different fill with no white, so a pointer passing over the rail
   can never be mistaken for the current screen.

   28px rows by default so eighteen entries and seven headings fit a 900px-tall viewport beside the
   brand and the workspace card; what does not fit scrolls, with the current entry brought into view
   (`AppShell.tsx`). A taller screen gets the roomier row. */
.navItem {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 26px;
  padding: 3px 6px 3px 8px;
  border-radius: var(--chip-r);
  font-size: var(--t-dense);
  font-weight: 500;
  line-height: 20px;
  color: var(--rail-ink-2);
  text-decoration: none;
  border: 0;
  background: none;
  cursor: pointer;
  text-align: left;
  width: 100%;
  /* Named, never `all`: `transition: all` animates layout properties off the GPU. */
  transition:
    background-color var(--dur-1) var(--ease-std),
    color var(--dur-1) var(--ease-std);
}

@media (min-height: 1000px) {
  .navItem {
    min-height: 30px;
  }
}

.navIcon {
  flex-shrink: 0;
  /* Quieter than the label until the row is the current one, so the icons scan as a column of
     marks and the current one stands out. */
  color: var(--rail-muted);
  transition: color var(--dur-1) var(--ease-std);
}

.navLabel {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.navItem:hover .navIcon {
  color: var(--rail-ink);
}

.navItem[data-active="true"] .navIcon {
  color: var(--rail-active-ink);
}

.navItem:hover {
  background: var(--rail-hover);
  color: var(--rail-ink);
}

/* Inside the box, not outside it: the strip is a scroll container, and a 2px outline with a 2px
   offset is cut off at its edge on the first and last rows. `--focus`, not the accent: the accent
   is the colour of the text on the current row and of every link. */
.navItem:focus-visible {
  outline: 2px solid var(--rail-accent);
  outline-offset: -2px;
}

/* The ring on the white current row: the pale rail ring was 1.41:1 there (WCAG 1.4.11 needs 3:1). */
.navItem[data-active="true"]:focus-visible {
  outline-color: var(--focus);
}

.navItem:active {
  background: var(--rail-card);
}

.navItem[data-active="true"] {
  background: var(--rail-active);
  color: var(--rail-active-ink);
  font-weight: 650;
}

.navItem[data-active="true"]:hover {
  background: var(--rail-active);
  color: var(--rail-active-ink);
}

/* The count on a rail entry (S-10, generalised in `RailBadges.tsx`). Right-aligned and quiet: a
   muted pill with a hairline, because three accent-filled pills down a rail shout about what is
   outstanding before the reader has chosen to care, and the rail is not where an alarm belongs.
   The ring is what keeps it visible on the hovered row, whose fill is the same grey. */
.navBadge {
  margin-left: auto;
  min-width: 20px;
  padding: 0 7px;
  border-radius: var(--radius-pill);
  background: var(--rail-badge);
  color: var(--rail-badge-ink);
  font-size: var(--t-min);
  font-variant-numeric: lining-nums tabular-nums;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}

.navItem[data-active="true"] .navBadge {
  background: var(--rail-active-ink);
  color: var(--rail-active);
}

/* An unread count is drawn in the warning colour, so a `?` cannot be taken for a number. */
.navBadge[data-state="unread"] {
  background: var(--warn-w);
  box-shadow: inset 0 0 0 1px var(--warn-line);
  color: var(--warn);
}

/* The workspace card at the foot of the rail: whose figures these are, in what jurisdiction and
   under which GWP set, and SAMPLE DATA where it applies. A demo figure must never be mistakable
   for a customer's, so the tag is the only warm thing in the rail and is never hidden or
   truncated. A card rather than a line under a rule: it is the one place the rail says anything
   about the data rather than the navigation, and it should not read as another entry. */
.workspace {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin: 2px 0 6px;
  padding: 8px 10px;
  border: 1px solid var(--rail-line);
  border-radius: var(--chip-r);
  background: var(--rail-card);
  font-size: var(--t-min);
  color: var(--rail-muted);
  line-height: 1.35;
  flex-shrink: 0;
}

/* The company card at the foot: the basis every figure is stated on, quiet. Not a control. */
.company {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 8px 10px;
  border: 1px solid var(--rail-line);
  border-radius: var(--chip-r);
  background: var(--rail-card);
  color: var(--rail-muted);
  font-size: var(--t-min);
  line-height: 1.35;
  flex-shrink: 0;
}

.companyLabel {
  font-weight: 600;
}

.companyMeta {
  color: var(--rail-ink-2);
  font-variant-numeric: lining-nums tabular-nums;
}

.workspaceName {
  display: block;
  max-width: 100%;
  overflow-wrap: anywhere;
  color: var(--rail-ink);
  font-size: var(--t-dense);
  font-weight: 600;
  line-height: 1.3;
}

.workspaceMeta {
  font-variant-numeric: lining-nums tabular-nums;
  letter-spacing: 0.01em;
}

.sampleTag {
  display: inline-block;
  margin-top: 4px;
  font-size: var(--t-min);
  font-weight: 700;
  line-height: 16px;
  color: var(--warn);
  background: var(--warn-w);
  border: 1px solid var(--warn-line);
  padding: 1px 7px;
  border-radius: var(--radius-pill);
}

/* At narrow widths this is a separate row between navigation and the main header. It cannot
   scroll away with either the navigation strip or the header's controls. The same pill carries
   the fact, and the desktop workspace card keeps its existing placement. */
.sampleStatus {
  flex: 0 0 auto;
  padding: 4px 10px;
  border-bottom: 1px solid var(--line);
  background: var(--panel);
}

.sampleStatus .sampleTag {
  margin-top: 0;
}

.switcher {
  width: 100%;
  margin-top: 8px;
  height: 28px;
  font-size: var(--t-min);
  padding: 0 6px;
  border: 1px solid var(--rail-line);
  border-radius: var(--radius-ctl);
  background: var(--rail-bg);
  color: var(--rail-ink-2);
  cursor: pointer;
}

.switcher:hover {
  border-color: var(--rail-muted);
  color: var(--rail-ink);
}

/* The collapsed workspace card. A letter tile and, where it applies, the tag - the tag is never
   dropped to make the rail narrower. The words wrap to two lines rather than abbreviate, because a
   marker that says "SMP" is a marker nobody reads. */
.workspaceMini {
  display: none;
}

.workspaceTile {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--rail-line);
  border-radius: var(--radius-ctl);
  background: var(--rail-card);
  color: var(--rail-ink);
  font-size: var(--t-dense);
  font-weight: 700;
}

/* A failed list of workspaces, said in the collapsed rail as a control that opens the full card
   where the retry lives. The alert is the full card's (`ReadFailed`); this only gets you to it. */
.workspaceWarn {
  display: grid;
  place-items: center;
  width: 32px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--warn-line);
  border-radius: var(--radius-ctl);
  background: var(--warn-w);
  color: var(--warn);
  cursor: pointer;
}

.workspaceWarn:hover {
  border-color: var(--warn);
}

/* The label that names a collapsed entry. Fixed-position, because the strip is a scroll container
   and clips anything that hangs outside it. Presentation only: the link's own text is its name. */
.tip {
  position: fixed;
  z-index: 40;
  padding: 4px 9px;
  border-radius: var(--radius-ctl);
  background: var(--ink);
  color: var(--on-fill);
  font-size: var(--t-min);
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  pointer-events: none;
  transform: translateY(-50%);
}

/* ---------------------------------------------------------------- collapsed rail */

/* Wide screens only. At 900px and below the rail is already a strip and has nothing to collapse
   to, so the preference is ignored there rather than half-applied. The width changes at once and is
   never animated: a width transition re-lays-out the whole page on every frame. */
@media (min-width: 901px) {
  .rail[data-collapsed="true"] {
    align-items: stretch;
    padding: 0 6px 10px;
  }

  .rail[data-collapsed="true"] .brandRow {
    justify-content: center;
    padding: 0;
  }

  .rail[data-collapsed="true"] .brandName {
    display: none;
  }

  /* A group's label is a heading for a list of words, and there are none. The hairline that ran
     out of it stays as the separator between chunks. */
  .rail[data-collapsed="true"] .navGroup {
    height: 1px;
    padding: 0;
    margin: 4px 6px;
    background: var(--rail-line);
    font-size: 0;
    line-height: 0;
    overflow: hidden;
  }

  .rail[data-collapsed="true"] .navGroup::after {
    display: none;
  }

  .rail[data-collapsed="true"] .navBlock + .navBlock {
    margin-top: 6px;
  }

  .rail[data-collapsed="true"] .navItem {
    justify-content: center;
    min-height: 34px;
    padding: 0;
  }

  /* Visually hidden, not removed: the label is the link's accessible name. `clip-path` without
     `overflow: hidden` so the browser gate's clipped-content scan has nothing to mistake for lost
     content. */
  .rail[data-collapsed="true"] .navLabel {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    clip-path: inset(50%);
  }

  /* A count rides the corner of its icon. */
  .rail[data-collapsed="true"] .navBadge {
    position: absolute;
    top: 1px;
    right: 3px;
    min-width: 16px;
    padding: 0 4px;
    line-height: 16px;
  }

  .rail[data-collapsed="true"] .workspace,
  .rail[data-collapsed="true"] .company {
    display: none;
  }

  .rail[data-collapsed="true"] .workspaceMini {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin: 2px 0 6px;
  }

  /* **A dot on the workspace tile, not a two-line pill** (design uplift U4). The words stay in the
     element - the gate and a screen reader read them - and are moved out of sight with an indent, so
     "Sample / data" no longer wraps in a 60px rail. */
  .rail[data-collapsed="true"] .sampleTag {
    width: 8px;
    height: 8px;
    margin-top: 0;
    padding: 0;
    overflow: hidden;
    border: 2px solid var(--warn);
    border-radius: var(--radius-pill);
    background: var(--warn-w);
    line-height: 8px;
    text-indent: -9999px;
    white-space: nowrap;
  }
}

/* ---------------------------------------------------------------- main */

.main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* White, like the bar: cards are told apart by their 1px border alone (KD, 2026-11-01). */
  background: var(--panel);
}

/* The top bar. **Rank, left to right:** the rail's control, where you are (the title with its
   purpose beside it), the one search, then - after the free space - which figures these are (the
   context group), the way to what is waiting, and who is looking (the person's menu, in
   `UserMenu.module.css`).

   Every item in the context group is context a figure cannot be read without - the method, the
   entity and the period - so none of them may be dropped at a narrow width: they shrink and
   ellipsise instead. The bar gains items as the product does, and at 820px it once overflowed by
   79px while the *page* did not, so `ui-check` measures the bar itself. `min-width: 0` on the
   flexible children is what lets an ellipsis actually fire: a flex item defaults to
   `min-width: auto`, which refuses to shrink below its content and pushes the overflow onto
   whatever comes last. */
.top {
  height: var(--top);
  flex: 0 0 var(--top);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px 0 12px;
  min-width: 0;
  background: var(--panel);
  border-bottom: 1px solid var(--line);
}

.spacer {
  flex: 1 1 0;
  min-width: 0;
}

/* A square control in the bar: the rail's toggle and the bell. 30px like every pill, so the bar has
   one control height. Hover, active and focus all differ from rest (F22). */
.iconButton {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: var(--chip-h);
  height: var(--chip-h);
  flex: 0 0 var(--chip-h);
  padding: 0;
  border: 1px solid var(--line-2);
  border-radius: var(--chip-r);
  background: var(--panel);
  color: var(--ink-2);
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color var(--dur-1) var(--ease-std),
    border-color var(--dur-1) var(--ease-std),
    color var(--dur-1) var(--ease-std);
}

.iconButton:hover {
  border-color: var(--muted);
  background: var(--panel-2);
  color: var(--ink);
}

.iconButton:active {
  background: var(--panel-3);
}

.iconButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.iconButton:disabled {
  color: var(--disabled);
  cursor: not-allowed;
}

/* The Menu button at narrow width (the rail becomes a sheet). A word beside the icon, because an
   icon alone is the hamburger nobody is sure of; a finger's height always, since it only exists on
   a screen a finger uses. */
.menuButton {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  min-height: var(--target-touch);
  padding: 0 12px;
  margin-left: auto;
  border: 1px solid var(--rail-line);
  border-radius: var(--chip-r);
  background: var(--rail-card);
  color: var(--rail-ink);
  font-size: var(--t-dense);
  font-weight: 600;
  cursor: pointer;
}

.menuButton:hover {
  background: var(--rail-hover);
}

.menuButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

/* The count on the bell. Over the corner, filled, so it reads as "this has something" at a glance.
   Decoration for sighted readers only (`aria-hidden`): the link's own name carries the number. */
.bellBadge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  padding: 0 4px;
  border-radius: var(--radius-pill);
  background: var(--accent);
  color: var(--on-fill);
  font-size: var(--t-min);
  font-variant-numeric: lining-nums tabular-nums;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.bellBadge[data-state="unread"] {
  background: var(--warn);
}

/* Where you are: the title and, beside it, its purpose in one line. The title is what the skip link
   and the document title name, and it is where focus lands after a navigation (F16), so it shows a
   ring when focused by keyboard like anything else. */
.heading {
  display: flex;
  /* **Gives up its room before the facts do** (responsive pass, 2026-10-30). At 1280px the entity
     read "All ..." and the period "Sierravale ..." — the year cut off — while the screen's purpose
     beside the title kept 90px: both shrank at the same rate, so the line that restates what the
     page already says outlasted the context a figure cannot be read without. The shrink factor
     empties the purpose first, then the title's own ellipsis, before the context group loses any. */
  flex: 0 8 auto;
  align-items: baseline;
  gap: 10px;
  min-width: 112px;
  overflow: hidden;
}

.title {
  flex: 0 1 auto;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  font-size: var(--t-head);
  font-weight: 650;
  letter-spacing: -0.01em;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: 4px;
}

/* The screen's purpose. A step quieter than the title, and the one thing in the bar that may be
   truncated or dropped: it restates what the title and the rail already say. */
.crumb {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--muted);
  font-size: var(--t-min);
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The one search (F20). Looks like the field it opens, so it reads as "search" at a glance, with
   the shortcut on it because a shortcut nobody is told about is not one. It is a button: the
   field that takes the typing is the palette's own, where the list is. */
.search {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: var(--chip-h);
  /* The shrink factor is large on purpose: when the bar is short of room the search gives way
     first, down to its floor, and only then do the facts beside it (period, entity) start to
     ellipsise. */
  flex: 0 8 420px;
  min-width: 140px;
  padding: 0 6px 0 10px;
  border: 1px solid var(--line-2);
  border-radius: var(--chip-r);
  background: var(--panel-2);
  color: var(--muted);
  font-size: var(--t-min);
  cursor: pointer;
  transition:
    background-color var(--dur-1) var(--ease-std),
    border-color var(--dur-1) var(--ease-std),
    color var(--dur-1) var(--ease-std);
}

.search:hover {
  border-color: var(--muted);
  background: var(--panel);
  color: var(--ink);
}

.search:active {
  background: var(--panel-3);
}

/* Beside the rail's toggle the heading (only for a screen with no page header yet) keeps to the
   search's right, so the order in the bar is the rail, the search, then where you are. */

.search:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.searchText {
  flex: 1;
  text-align: left;
}

.searchKey {
  padding: 0 5px;
  border: 1px solid var(--line-2);
  border-radius: 4px;
  background: var(--panel);
  color: var(--muted);
  font: inherit;
  font-family: var(--mono);
  font-size: var(--t-min);
  line-height: 16px;
}

/* Below this the field becomes a square: the icon is the affordance and the shortcut still works.
   The context group needs the room, and each of its items is a fact a figure cannot be read
   without. */
@media (width <= 1280px) {
  .search {
    flex: 0 0 var(--chip-h);
    min-width: var(--chip-h);
    width: var(--chip-h);
    justify-content: center;
    padding: 0;
  }

  .searchText,
  .searchKey {
    display: none;
  }
}

/* The purpose is the first thing to go when the bar is short of room. */
@media (width <= 1180px) {
  .crumb {
    display: none;
  }
}

/* **The context group** - the method, the entity and the period. Separate pills at one height,
   because three facts are three things; the entity has no chrome at all since it is not a control. */
.context {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  /* Keeps its natural width where the bar has room, so the period and entity are read whole; the
     title and the search give way first. The facts can shrink with an ellipsis rather than
     pushing the person's menu outside the bar. */
  flex: 0 1 auto;
}

.chipIcon {
  flex-shrink: 0;
  color: var(--muted);
}

/* The entity is a statement, not a control (F21): no border, no fill, no hover, no pointer. Shrinks
   first, with an ellipsis - a truncated name is still a name, and a dropped one is a figure about
   nobody. */
.entity {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 240px;
  padding: 0 4px;
  color: var(--ink-2);
  font-size: var(--t-min);
  font-weight: 600;
  line-height: 16px;
}

/* A block, not a flex row: `text-overflow` only applies to the text of a block container, and a
   flex row cut the name mid-letter with no ellipsis. */
.entityText {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entityNote {
  margin-left: 8px;
  color: var(--warn);
  font-size: var(--t-min);
  font-weight: 500;
}

/* The reporting-period selector. **Always a select** (EXP-21): a custom range is one of its
   options, so there is always more than one and always a choice. A bordered pill, because it is a
   control - and it changes border on hover, which a bare native select did not (F22).

   Native control, native menu, so the platform's own accessibility comes with it. The chevron is
   an icon laid over the select's right edge (`pointer-events: none`, so a click still opens it),
   with `appearance: none` removing the platform's own arrow beside ours. The whole part is the
   hit area. */
.periodChip {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: var(--chip-h);
  flex: 0 1 auto;
  min-width: 0;
  /* The outline is drawn **inside** the box, not as a border: a 1px border takes 2px from the content
     box, so the select - the thing a finger lands on - was `--chip-h` minus 2 (42px at the phone's 44px)
     while the pill around it was 44. With the outline as a shadow the select is the pill's whole height. */
  border: 0;
  box-shadow: inset 0 0 0 1px var(--line-2);
  border-radius: var(--chip-r);
  background: var(--panel);
  cursor: pointer;
  transition:
    background-color var(--dur-1) var(--ease-std),
    border-color var(--dur-1) var(--ease-std);
}

.periodChip:hover {
  box-shadow: inset 0 0 0 1px var(--muted);
  background: var(--panel-2);
}

.periodChip .chipIcon {
  position: absolute;
  left: 10px;
  pointer-events: none;
}

.periodCaret {
  position: absolute;
  right: 9px;
  color: var(--muted);
  pointer-events: none;
}

.period {
  appearance: none;
  height: 100%;
  max-width: 240px;
  /* Shrinks before anything is dropped. A truncated period label is still a period; an absent one
     is a figure with no year against it. */
  min-width: 70px;
  flex-shrink: 1;
  padding: 0 26px 0 31px;
  border: 0;
  border-radius: inherit;
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: var(--t-min);
  font-weight: 600;
  font-variant-numeric: lining-nums tabular-nums;
  text-overflow: ellipsis;
  cursor: pointer;
}

/* Inside the part: a ring drawn outside a 30px-high pill would overlap the bar's own border. */
.period:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -3px;
}

/* The custom reporting window (EXP-21). The form replaces the period selector, so it is sized
   like it: the context bar's own overflow is measured at 820px and every other item in it is a
   fact a figure cannot be read without. */
.range {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-shrink: 1;
  padding: 0 4px 0 6px;
}

.rangeDate {
  height: 28px;
  padding: 0 6px;
  border-radius: var(--radius-ctl);
  border: 1px solid var(--line-2);
  background: var(--panel);
  color: var(--ink);
  font-size: var(--t-min);
  font-family: var(--font);
  font-variant-numeric: lining-nums tabular-nums;
}

.rangeDate:hover {
  border-color: var(--muted);
}

.rangeApply,
.rangeCancel {
  height: 28px;
  flex-shrink: 0;
  padding: 0 11px;
  border-radius: var(--radius-ctl);
  font-size: var(--t-min);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color var(--dur-1) var(--ease-std),
    border-color var(--dur-1) var(--ease-std);
}

.rangeApply {
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--on-fill);
}

.rangeApply:hover {
  border-color: var(--accent-h);
  background: var(--accent-h);
}

.rangeApply:active {
  background: var(--ink);
  border-color: var(--ink);
}

.rangeCancel {
  border: 1px solid var(--line-2);
  background: var(--panel);
  color: var(--ink-2);
  font-weight: 500;
}

.rangeCancel:hover {
  border-color: var(--muted);
  background: var(--panel-2);
  color: var(--ink);
}

.rangeCancel:active {
  background: var(--panel-3);
}

/* A refusal, said beside the control that was refused. It is allowed to be the thing that makes the
   bar scroll at a narrow width: a sentence a reader has to see beats a sentence that fits. */
.rangeRefused {
  color: var(--crit);
  font-size: var(--t-min);
  max-width: 320px;
}

/* "From" and "To" in words beside each date (F17). Muted, because the date is the value. */
.rangeField {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 500;
}

.rangeDate:focus-visible,
.rangeApply:focus-visible,
.rangeCancel:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

/* ---------------------------------------------------------------- body grid */

.body {
  flex: 1;
  min-height: 0;
  padding: var(--gap) var(--pad);
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  /* The KPI strip hugs its content; the panel row takes everything left. `minmax(0, 1fr)`
     rather than `1fr`, because a `1fr` row also defaults to a minimum of auto and would grow
     past the viewport the moment a panel's content did - which is the page scroll this shell
     exists to avoid. */
  grid-template-rows: auto minmax(0, 1fr);
  gap: var(--gap);
  overflow: hidden;
}

/* ---------------------------------------------------------------- narrow */

/* At phone width the rail becomes a horizontal strip and the grid collapses to one column. The
   page still does not scroll: the body scrolls instead, because at 400px a fixed-height grid
   would clip content rather than reveal it. `make ui-check` asserts no *horizontal* overflow
   here, which is the failure that actually breaks a layout. */
@media (max-width: 900px) {
  .shell {
    flex-direction: column;
  }

  /* The rail is a menu at this width: the slot is transparent, so the rail is a flex item of the
     shell exactly as the strip was. */
  .railSlot {
    display: contents;
  }

  /* **The rail is a menu at this width** (F6). Closed it is one row, the brand and a Menu button, so
     the screen keeps its height (the strip it replaces was a second scrolling row that nobody knew
     to swipe). Open it is a full-screen sheet with the workspace, every place in its group and the
     reporting basis, and the rest of the shell is made inert behind it (`AppShell.tsx`).

     **It is still its own containing block (F-053).** `overflow` clips an absolutely-positioned
     descendant only when the scroller is - or sits inside - that descendant's containing block, and
     with no positioned ancestor the containing block is the viewport. The visually-hidden words on a
     count badge (`position: absolute`) were therefore *not* clipped by the strip they sit in: their
     static position counted towards the document's scroll width and every screen overflowed. The
     sheet is `position: fixed`, which is also a containing block, so the fix survives; the closed
     row keeps `position: relative` for the same reason. */
  .rail {
    position: relative;
    width: 100%;
    flex: 0 0 auto;
    flex-flow: row wrap;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-right: 0;
    overflow-x: hidden;
  }

  .rail[data-menu="open"] {
    position: fixed;
    inset: 0;
    z-index: 40;
    flex-flow: column nowrap;
    align-items: stretch;
    gap: 8px;
    overflow-y: auto;
    padding-bottom: 20px;
    background: var(--rail-bg);
  }

  .rail[data-menu="closed"] > :not(.brandRow) {
    display: none;
  }

  .brandRow {
    height: auto;
    min-height: var(--target-touch);
    flex: 1 1 auto;
    padding: 0 6px 0 0;
  }

  .rail[data-menu="open"] .brandRow {
    flex: 0 0 auto;
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--rail-bg);
  }

  /* The strip is already as small as it gets, so there is nothing to collapse to. */
  .railToggle {
    display: none;
  }

  /* The sheet's list: one column, the groups headed, every row a finger wide. */
  .nav {
    position: relative;
    flex: 0 0 auto;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
    overflow: visible;
    padding: 0;
    background: none;
  }

  .navBlock + .navBlock {
    margin-top: 8px;
  }

  .navItem {
    min-height: var(--target-touch);
    font-size: var(--t-body);
  }

  .workspaceMini {
    display: none;
  }

  .body {
    grid-template-columns: minmax(0, 1fr);
    /* Content height, not viewport height: at this width the body scrolls, so a 1fr row would
       clip a panel rather than let it be read. */
    grid-template-rows: none;
    grid-auto-rows: auto;
    overflow-y: auto;
  }

  /* **The context bar wraps; it no longer scrolls sideways** (responsive pass, 2026-10-30).
     Measured at 390px on every one of the thirty screens: the bar was 594 to 798px wider than the
     screen, so the period, the entity, the bell and **the person's menu, which holds Sign out**,
     sat off the right edge of a bar nobody knew to swipe — and at 720px (200% zoom of 1440) the
     entity and period had shrunk to a lone "." and "S.", which is a fact cut to nothing. A
     scrolling bar also clipped the menus that open beneath it: `overflow-x: auto` makes
     `overflow-y` auto too. Every item here is context a figure cannot be read without, so none may
     be hidden by a scroll position; a second row costs height, the cheap thing at this width. The
     bar grows to hold its rows rather than fixing its height. */
  .top {
    flex: 0 0 auto;
    flex-wrap: wrap;
    height: auto;
    min-height: var(--top);
    padding-block: 6px;
    row-gap: 6px;
    overflow: visible;
  }

  /* The group wraps inside itself too, so the method, entity and period keep their full width on
     whatever line they land on instead of shrinking to an ellipsis. */
  .context {
    flex: 1 1 100%;
    order: 3;
    flex-wrap: wrap;
    row-gap: 6px;
  }

  /* The person's menu stays at the right edge when it wraps onto a row of its own. */
  .top > :last-child {
    margin-left: auto;
  }

  .crumb {
    display: none;
  }

  /* The title, the search, the bell and the person's menu share the first row - where you are and
     who you are - and the reporting context follows beneath, whole. `order` rather than moving the
     markup, so document and tab order stay what the desktop's is. This was a phone rule; at tablet
     width the person's menu was wrapping onto a row of its own beside an empty one (F6). */
  .heading {
    flex: 0 1 auto;
    order: 0;
  }

  .search {
    order: 0;
  }

  .spacer {
    order: 1;
  }

  .top > :nth-last-child(-n + 3) {
    order: 2;
  }

}

/* A phone. The title, the search, the bell and the person's menu share the first row — where you
   are and who you are — and the reporting context follows beneath, whole. `order` rather than
   moving the markup, so document and tab order stay what the desktop's is. */
@media (max-width: 560px) {
  /* **Thumb-sized controls in the chrome** (responsive pass, 2026-10-30). Measured at 390px: the
     rail's entries were 28px tall, the method toggle's buttons 24px, and the search, bell and
     period 30px — all under the 44px a finger needs, on the controls used on every screen. The
     pills share one height (`--chip-h`), so one number moves them all; the toggle's buttons and the
     rail's entries are sized directly. Desktop is untouched: this is the width a phone has. */
  .shell {
    --chip-h: 44px;
  }

  .navItem {
    min-height: 44px;
  }

  /* The title may shrink to an ellipsis on a phone so the bell and the person's menu stay on the
     first row beside it; at 112px it pushed the menu onto a row of its own. */
  .heading {
    /* A basis of 64px, not "auto": a line is broken on each item's basis, so a long title kept the
       person's menu on a row of its own however far it could have shrunk. It takes the free room
       itself (the spacer is not needed) and ends in an ellipsis. */
    flex: 1 1 64px;
    min-width: 64px;
  }

  .spacer {
    display: none;
  }

  .top {
    gap: 8px;
    padding-inline: 12px;
  }

}

/* A short viewport keeps the desktop columns and must still scroll (UX-03). At 200% zoom a wide
   screen stays wider than 900px while losing more than half its height, so the width-based rule
   above never fires and the panel row becomes `minmax(0, 1fr)` of almost nothing. `Panel.module.css`
   releases the panels' own scrollers at the same breakpoint; this is what gives the page somewhere
   to put them. */
@media (max-height: 620px) {
  .body {
    grid-template-rows: none;
    grid-auto-rows: auto;
    overflow-y: auto;
  }
}
